/**
 * Statistical utilities for diagnostic analysis
 */

export function computeStats(values: number[]): {
  min: number;
  max: number;
  avg: number;
  median: number;
  p50: number;
  p90: number;
  p95: number;
  p99: number;
  stdDev: number;
  count: number;
} {
  if (values.length === 0) {
    return { min: 0, max: 0, avg: 0, median: 0, p50: 0, p90: 0, p95: 0, p99: 0, stdDev: 0, count: 0 };
  }

  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;
  const sum = sorted.reduce((a, b) => a + b, 0);
  const avg = sum / n;

  const variance = sorted.reduce((acc, v) => acc + (v - avg) ** 2, 0) / n;

  return {
    min: sorted[0],
    max: sorted[n - 1],
    avg: Math.round(avg * 100) / 100,
    median: percentile(sorted, 50),
    p50: percentile(sorted, 50),
    p90: percentile(sorted, 90),
    p95: percentile(sorted, 95),
    p99: percentile(sorted, 99),
    stdDev: Math.round(Math.sqrt(variance) * 100) / 100,
    count: n,
  };
}

export function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  const idx = (p / 100) * (sorted.length - 1);
  const lower = Math.floor(idx);
  const upper = Math.ceil(idx);
  if (lower === upper) return Math.round(sorted[lower] * 100) / 100;
  const frac = idx - lower;
  return Math.round((sorted[lower] * (1 - frac) + sorted[upper] * frac) * 100) / 100;
}

export function computeJitter(rtts: number[]): number {
  if (rtts.length < 2) return 0;
  let totalDiff = 0;
  for (let i = 1; i < rtts.length; i++) {
    totalDiff += Math.abs(rtts[i] - rtts[i - 1]);
  }
  return Math.round((totalDiff / (rtts.length - 1)) * 100) / 100;
}

/**
 * MOS (Mean Opinion Score) - simplified E-model
 * Range: 1.0 (bad) to 5.0 (excellent)
 */
export function computeMOS(avgLatencyMs: number, jitterMs: number, lossPercent: number): number {
  let r = 93.2 - avgLatencyMs / 40 - jitterMs * 2.5 - lossPercent * 2.5;
  r = Math.max(0, Math.min(100, r));
  let mos = 1 + 0.035 * r + r * (r - 60) * (100 - r) * 7e-6;
  return Math.max(1, Math.min(5, Math.round(mos * 100) / 100));
}

export function computeFlowStability(throughputs: number[]): number {
  if (throughputs.length < 2) return 1;
  const stats = computeStats(throughputs);
  if (stats.avg === 0) return 0;
  const cv = stats.stdDev / stats.avg;
  return Math.round((1 - Math.min(cv, 1)) * 100) / 100;
}

export function detectCongestion(rtts: number[], throughputs: number[]): number {
  if (rtts.length < 5 || throughputs.length < 5) return 0;

  const mid = Math.floor(rtts.length / 2);
  const firstHalfRTT = computeStats(rtts.slice(0, mid));
  const secondHalfRTT = computeStats(rtts.slice(mid));
  const rttTrend = firstHalfRTT.avg > 0 ? (secondHalfRTT.avg - firstHalfRTT.avg) / firstHalfRTT.avg : 0;

  const tpMid = Math.floor(throughputs.length / 2);
  const firstTP = computeStats(throughputs.slice(0, tpMid));
  const secondTP = computeStats(throughputs.slice(tpMid));
  const tpTrend = firstTP.avg > 0 ? (firstTP.avg - secondTP.avg) / firstTP.avg : 0;

  return Math.min(Math.round((Math.max(rttTrend, 0) * 0.6 + Math.max(tpTrend, 0) * 0.4) * 100) / 100, 1);
}

export function detectISPShaping(throughputs: number[]): boolean {
  if (throughputs.length < 10) return false;
  const stats = computeStats(throughputs);
  const lastQuarter = throughputs.slice(Math.floor(throughputs.length * 0.75));
  const lastStats = computeStats(lastQuarter);
  return stats.max > 0 && lastStats.avg < stats.max * 0.5;
}

export function computeBufferHealth(chunks: { gap: number; duration: number }[]): number {
  if (chunks.length === 0) return 1;
  const avgGap = chunks.reduce((sum, c) => sum + c.gap, 0) / chunks.length;
  const avgDuration = chunks.reduce((sum, c) => sum + c.duration, 0) / chunks.length;
  if (avgDuration === 0) return 1;
  // Buffer health: ratio of useful transfer time vs gaps
  const ratio = avgDuration / (avgDuration + avgGap);
  return Math.round(Math.max(0, Math.min(1, ratio)) * 100) / 100;
}

export function getGrade(mos: number): 'A' | 'B' | 'C' | 'D' | 'F' {
  if (mos >= 4.3) return 'A';
  if (mos >= 4.0) return 'B';
  if (mos >= 3.6) return 'C';
  if (mos >= 3.1) return 'D';
  return 'F';
}

export function formatSpeed(mbps: number): string {
  if (mbps >= 1000) return `${(mbps / 1000).toFixed(1)} Gbps`;
  if (mbps >= 1) return `${mbps.toFixed(1)} Mbps`;
  return `${(mbps * 1000).toFixed(0)} Kbps`;
}

export function formatLatency(ms: number): string {
  if (ms < 1) return `${(ms * 1000).toFixed(0)} µs`;
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)} s`;
  return `${ms.toFixed(1)} ms`;
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1_073_741_824) return `${(bytes / 1_073_741_824).toFixed(1)} GB`;
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}
